import { Button, pxToRem, useStyles, useTheme } from '@archipro-design/aria';
import { ArrowLeftSLine } from '@archipro-design/icons';
import * as S from './DetailBannerSection.style';
import type {
    ColumnDimensionType,
    TopBanner,
} from '~/modules/page-builder/types';
import ImageWithPaintPickerArticle from '../../common/image-with-paint-picker/ImageWithPaintPickerArticle';
import { useClickBack } from '~/modules/root/hook/use-click-back';
import React from 'react';
import VideoBannerWithOptimistic from '@modules/page-builder/component/edit-article/edit-top-banner/VideoBannerWithOptimistic';
import { useLocation } from '@remix-run/react';
import ImageCaptionArticle from '~/modules/page-builder/component/common/image-caption/ImageCaptionArticle';
import { ImageCaptionContainer } from '@modules/page-builder/component/detail-article/detail-single-image-shell/DetailSingleImageShell.style';
import type { AwardProps } from '@modules/page-builder/component/detail-article/banner-awards/types';
import BannerAwards from '@modules/page-builder/component/detail-article/banner-awards/BannerAwards';
import { hasFeature, useAppData } from '@modules/root';
import { BoostResolutionOverlay } from '@modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';

interface DetailBannerSectionProps extends TopBanner {
    dimensions: ColumnDimensionType;
    size: string;
    title?: string;
    actions?: React.ReactNode[];
    showBackButton?: boolean;
    showActionsOnHover?: boolean;
    caption?: string;
    awards?: AwardProps[];
    customOverlayElement?: React.ReactNode;
    showOverlay?: boolean;
}

const DetailBannerSection = ({
    dimensions,
    imageUrl,
    introText,
    libraryImage,
    size,
    title,
    actions = [],
    showBackButton,
    showActionsOnHover = false,
    videoBannerUrl,
    isVideoBanner,
    caption,
    awards,
    customOverlayElement,
    showOverlay = false,
}: DetailBannerSectionProps) => {
    const { css } = useStyles({
        dimension: dimensions,
        showActionsOnHover,
        showOverlay,
    });
    const theme = useTheme();
    const { user } = useAppData();
    const enableAwardBadges = hasFeature(user.FeatureFlags, 'award_badges');

    const { onClick: onClickBack } = useClickBack();

    const altText =
        caption ?? introText ?? (title ? `${title} banner` : 'banner');

    const location = useLocation();
    const urlSearchParams = new URLSearchParams(location.search);

    // inline autoplay (default)
    const urlVideoParam = urlSearchParams.get('video');
    const autoPlayBanner = Number(urlVideoParam) === 1;

    // modal autoplay
    const urlModalVideoParam = urlSearchParams.get('modal-video');
    const autoPlayModalBanner = Number(urlModalVideoParam) === 1;

    const canBoostBanner = !isVideoBanner && !!imageUrl && !!libraryImage?.ID;

    const actionsSlot = actions.length > 0 ? <>{...actions}</> : undefined;

    const bannerContent = (
        <div className={css(S.BannerWrapper)} id={'top-banner'}>
            <div className={css(S.ImageContainer)}>
                {imageUrl && (
                    <>
                        {isVideoBanner && videoBannerUrl ? (
                            <VideoBannerWithOptimistic
                                dimension={dimensions}
                                videoBannerUrl={videoBannerUrl}
                                imageUrl={imageUrl}
                                playsInline={!autoPlayModalBanner}
                                autoplay={autoPlayBanner || autoPlayModalBanner}
                            />
                        ) : (
                            <ImageWithPaintPickerArticle
                                key={imageUrl}
                                src={imageUrl}
                                layout={'fill'}
                                placeholderColor="light"
                                width={dimensions.width}
                                height={dimensions.height}
                                loading={'eager'}
                                sizes={size}
                                objectFit={'cover'}
                                alt={altText}
                            />
                        )}
                    </>
                )}
                {showBackButton && (
                    <>
                        <Button
                            as="a"
                            transparent={true}
                            icon={
                                <ArrowLeftSLine
                                    variables={{
                                        color: theme.siteVariables.colors
                                            .white[100],
                                    }}
                                />
                            }
                            variables={{
                                buttonPadding: pxToRem(12),
                                iconPadding: 0,
                            }}
                            size={30}
                            className={css(S.BackButton)}
                            onClick={onClickBack}
                        />
                    </>
                )}
                {enableAwardBadges && awards && awards.length > 0 && (
                    <BannerAwards awards={awards} />
                )}
                {!canBoostBanner && (
                    <div role="toolbar" className={css(S.HoverActions)}>
                        {...actions}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <>
            {canBoostBanner ? (
                <BoostResolutionOverlay
                    libraryImageId={libraryImage.ID}
                    currentImageUrl={imageUrl}
                    displayWidth={dimensions.width}
                    displayHeight={dimensions.height}
                    wasUpscaled={!!libraryImage.WasUpscaled}
                    buttonClassName="bottom-8 right-8"
                    actionSlot={actionsSlot}
                >
                    {bannerContent}
                </BoostResolutionOverlay>
            ) : (
                bannerContent
            )}
            {caption && (
                <div className={css(S.MobileCaptionContainer)}>
                    {caption && (
                        <div className={css(ImageCaptionContainer)}>
                            <ImageCaptionArticle text={caption} isMain={true} />
                        </div>
                    )}
                </div>
            )}
            {customOverlayElement}
        </>
    );
};

export default DetailBannerSection;
